/**
 * /destinations/add — connect a webhook destination (plan §7.1). Connect is
 * probe-first: the server posts a labeled test message to the channel before
 * it stores anything, so a saved destination always works.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { PageHeader } from '@/components/patterns/page-header';
import { Button, buttonVariants } from '@/components/ui/button';
import { ApiError, destinationsApi } from '@/lib/api';
import { qk } from '@/lib/query-client';
import { cn } from '@/lib/utils';

const TIME_PATTERN = /^([01]\d|2[0-3]):[0-5]\d$/;

export function AddDestinationPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [url, setUrl] = useState('');
  const [label, setLabel] = useState('');
  const [time, setTime] = useState('18:00');
  const [tz] = useState(() => Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC');
  const [postSummary, setPostSummary] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const connect = useMutation({
    mutationFn: () =>
      destinationsApi.connect({
        url: url.trim(),
        format: 'slack',
        ...(label.trim() ? { label: label.trim() } : {}),
        schedule: { tz, runs: [{ at: time }] },
        postSummary,
      }),
    onSuccess: (d) => {
      void qc.invalidateQueries({ queryKey: qk.destinations });
      toast.success('Connected — check the channel for the test message.');
      void navigate({ to: '/destinations/$destinationId', params: { destinationId: d.id } });
    },
    onError: (err) => {
      // The server maps URL/SSRF/probe failures to human-readable reasons
      // (plan D12) — show the reason, not the code.
      const body = err instanceof ApiError ? (err.body as { reason?: string } | null) : null;
      setError(body?.reason ?? err.message ?? 'Could not connect that webhook.');
    },
  });

  const valid = url.trim().startsWith('https://') && TIME_PATTERN.test(time);

  return (
    <div className="max-w-xl">
      <Link
        to="/destinations"
        className={cn(buttonVariants({ variant: 'ghost', size: 'sm' }), 'mb-4 -ml-2')}
      >
        <ArrowLeft className="h-4 w-4" />
        Destinations
      </Link>
      <PageHeader
        title="Add destination"
        subtitle="Paste a Slack incoming webhook URL. Brain verifies it by posting a test message to the channel before saving anything."
      />

      <div className="mt-8 flex flex-col gap-6 rounded-xl border border-border p-6">
        <div>
          <label htmlFor="webhook-url" className="text-sm font-medium">
            Webhook URL
          </label>
          <input
            id="webhook-url"
            type="password"
            autoComplete="off"
            placeholder="https://hooks.slack.com/services/T…/B…/…"
            value={url}
            onChange={(e) => {
              setUrl(e.target.value);
              setError(null);
            }}
            className="mt-2 w-full rounded-lg border border-border bg-background px-3 py-2 font-mono text-sm outline-none focus:border-primary"
          />
          <p className="mt-2 text-[13px] text-faint">
            In Slack: channel settings → Integrations → Apps → Incoming Webhooks → Add. The URL is a
            secret — it&apos;s stored encrypted and never shown again.
          </p>
        </div>

        <div>
          <label htmlFor="destination-label" className="text-sm font-medium">
            Label <span className="font-normal text-faint">(optional)</span>
          </label>
          <input
            id="destination-label"
            type="text"
            placeholder="#client-acme proposals"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            maxLength={80}
            className="mt-2 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary"
          />
        </div>

        <div>
          <label htmlFor="destination-time" className="text-sm font-medium">
            Daily run time <span className="font-normal text-faint">({tz})</span>
          </label>
          <input
            id="destination-time"
            type="time"
            value={time}
            onChange={(e) => setTime(e.target.value)}
            className="mt-2 rounded-lg border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary"
          />
          <p className="mt-2 text-[13px] text-faint">
            Brain distills the day&apos;s messages and posts proposals at this time. Dry-run first:
            review what it would post before anything goes to the channel.
          </p>
        </div>

        <label className="flex items-start gap-3 text-sm">
          <input
            type="checkbox"
            checked={postSummary}
            onChange={(e) => setPostSummary(e.target.checked)}
            className="mt-0.5"
          />
          <span>
            Post a run summary
            <span className="block text-[13px] text-faint">
              One message per run with counts and warnings — so a quiet day never reads as a broken
              pipeline.
            </span>
          </span>
        </label>

        {error ? (
          <p className="rounded-lg border border-destructive-border bg-destructive-bg px-3 py-2 text-sm text-destructive">
            {error}
          </p>
        ) : null}

        <Button disabled={!valid || connect.isPending} onClick={() => connect.mutate()}>
          {connect.isPending ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Verifying with a test message…
            </>
          ) : (
            'Connect destination'
          )}
        </Button>
      </div>
    </div>
  );
}
